import {
  ComparatorContext,
  ComponentDescriptorOrArray,
  IProduct,
  getRendererFromDescriptor,
  useField,
  usePage,
} from '@kizi/comparator-core';
import {
  Button,
  Div,
  DivProps,
  Heading,
  Image,
  P,
} from '@kizi/core-components';
import { Checkbox } from '@kizi/shared-components';
import { get } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Desktop } from '../../utils/breakpoint';
import { isMobile, useHandleSubmitClick } from '../../utils/global';
import { PriceMode, getClassesByState } from '../../utils/product';
import { getLabel } from '../../utils/translations';
import { ButtonCard } from '../ButtonCard';
import { CountdownProps } from '../Countdown';
import { Price, PriceProps } from '../Price';
import {
  Arrow,
  NonPurchasableText,
  ResetFiltersBlock,
  addMoreProducts,
} from '../Product';
import { ProductOverviewModal } from '../ProductOverviewModal';
import { ProductUsps, ProductUspsProps } from '../ProductUsps';
import { PromotionBar, PromotionBarProps } from '../PromotionBar';
import { RatingReviews } from '../RatingReviews';
import { Requirement } from '../Requirement';
import { ProductSkeletonCard } from '../Skeletons/ProductSkeleton';
import {
  Sustainability,
  SustainabilityProps,
  getDisplayedScore,
} from '../Sustainability';
import { Window } from '../Window';

import { sendCustomEvent } from '../wrappers/TabViewWizard/ApplyPageWizard';
import { AdditionalInfo, AdditionalInfoProps } from './AdditionalInfo';
import { ExpandedPart } from './ExpandedPart';
import useTabTracking from '../../utils/hooks';
import { getRiskProductTestScore } from '../../utils/helpers';

export type TabContentProps = {
  label: string;
  content: { type: string };
};

export interface ProductNewProps extends DivProps {
  data: IProduct;
  priceMode: PriceMode;
  promotionModals?: PromotionBarProps;
  additionalInfo?: AdditionalInfoProps;
  sustainabilityProps?: SustainabilityProps;
  priceProps?: PriceProps;
  uspsProps?: ProductUspsProps;
  countdownProps?: CountdownProps;
  nextIndex?: number;
  showSubscriptionName?: boolean;
  productTypes?: Array<string>;
  tariffsTab?: React.ReactNode;
  multiCompare?: boolean;
  tabsContent?: TabContentProps[];
  customProductContent?: any;
  hideTestoordeel: boolean;
  registerAnalyticsAddToCartEvent?: any;
  comparatorName: string;
  indexInProductList: number;
  getTabs?: any;
  hideProductUsps?: boolean;
  registerAnalyticsViewItemEvent?: any;
  registerInPeregrineEvent?: any;
  checkoutInPeregrineEvent?: any;
}

export const getBigProductFeature = (data: IProduct, priceMode: PriceMode) => {
  const { bigProductFeature } = data;

  const { value, ...restBigProductFeatureProps } = bigProductFeature;

  return {
    ...restBigProductFeatureProps,
    value: value[priceMode],
  };
};

interface DiscountTileProps extends DivProps {
  variant?: string;
  discount: {
    description: string;
    value: string;
  };
}

export const DiscountTile = ({
  variant = 'captionRegular',
  discount,
  ...props
}: DiscountTileProps) => {
  return (
    <Div
      px={1}
      py={1}
      borderRadius="8px"
      maxWidth="fit-content"
      backgroundColor="#FFC60D"
      {...props}
    >
      <P
        variant={variant}
        textAlign="center"
        lineHeight={variant === 'captionRegular' ? '14px' : '24px'}
      >
        {discount.value}
      </P>
    </Div>
  );
};

export const ConfirmationContent = ({
  providerName,
  showDiscountTile,
  isMobile,
  handleSubmit,
  showModal,
  data,
}: {
  providerName: string;
  showDiscountTile: boolean;
  isMobile: () => boolean;
  handleSubmit: () => void;
  showModal: () => void;
  data: any;
}) => {
  const welcomeDiscount = data?.customAttributes?.find(
    (el) => el.description === 'Welcome Discount',
  );
  return (
    <Div>
      {!isMobile() && (
        <Heading variant="H4" as="h4" color="heading" mt={0} textAlign="center">
          {`Heb je een ${providerName} rekening?`}
        </Heading>
      )}
      {providerName === 'ABN AMRO' ? (
        <P variant="bodyRegular">
          {
            'Voor deze verzekering is een particuliere betaalrekening van ABN AMRO benodigd. De rekening dient op naam van de aanvrager van de verzekering te staan. Heb je deze niet? Dan is het niet mogelijk een verzekering af te sluiten.'
          }
        </P>
      ) : (
        <>
          <P variant="bodyRegular" mb={2}>
            {
              'Particuliere ING betaalrekening op naam van de aanvrager van deze verzekering vereist.'
            }
          </P>
          <P variant="bodyRegular" lineHeight="24px">
            {
              'Heb je geen ING betaalrekening? Dan is het niet mogelijk een verzekering af te sluiten via ING.'
            }
          </P>
        </>
      )}
      {showDiscountTile && (
        <DiscountTile mt={2} variant="bodyRegular" discount={welcomeDiscount} />
      )}
      <Div display="flex" flexDirection="column" alignItems="center">
        <Button
          onClick={handleSubmit}
          variant="primary"
          mt={4}
          mb={4}
          width="100% !important"
        >
          {providerName === 'ABN AMRO'
            ? 'Ja, heb ik!'
            : 'Ja heb ik, verder met aanvragen'}
        </Button>
        <Button onClick={showModal} variant="transparent" mb={4}>
          {providerName === 'ABN AMRO'
            ? 'Nee, kies alternatief'
            : 'Nee heb ik niet, alternatief kiezen'}
        </Button>
      </Div>
    </Div>
  );
};
export interface InnerModalComponentProps {
  shouldOpenNewTab: boolean;
  data: any;
  nextIndex: any;
  registerAnalyticsAddToCartEvent: () => void;
  providerName: string;
  onClose: () => void;
  onSubmitFn?: (service: any, data: any) => void;
}

interface PriceButtonCompProps {
  type: string;
  discountIncluded: number;
  priceInitialValue: number;
  priceMode: string;
  priceValue: number;
  data: any;
  priceProps: any;
  purchasableProduct: boolean;
  shouldOpenApplyModal: boolean;
  showConfirmationModal: boolean;
  comparatorName: string;
  providerName: string;
  shouldShowModal: (showConfirmationModal: boolean) => void;
  hideModal: () => void;
  handleSubmit: () => void;
  isOverstappen: boolean;
  showDiscountTile: boolean;
  hideCTAButton?: boolean;
}

const getLabelText = (priceMode, type) => {
  if (type === 'solarPanels') {
    return 'Incl. installatie';
  }

  if (type === 'savings') {
    return 'rentepercentage';
  }

  return `per ${getLabel(priceMode)}`;
};

const PriceButtonComp: React.FC<PriceButtonCompProps> = React.memo(
  ({
    type,
    discountIncluded,
    priceInitialValue,
    priceMode,
    priceValue,
    data,
    priceProps,
    purchasableProduct,
    shouldOpenApplyModal,
    shouldShowModal,
    hideModal,
    showConfirmationModal,
    handleSubmit,
    providerName,
    isOverstappen,
    showDiscountTile,
    hideCTAButton = false,
  }) => {
    return (
      <Div display="flex" flexDirection="column">
        <Div display="flex" flexDirection={'column'} gridGap={'4px'}>
          <Price
            discount={discountIncluded}
            initialValue={priceInitialValue}
            label={getLabelText(priceMode, type)}
            value={priceValue}
            variant="priceBig"
            type={type}
            data={data}
            {...priceProps}
          />
        </Div>

        {!hideCTAButton && (
          <>
            {purchasableProduct ? (
              <Div display={type === 'solarPanels' ? 'flex' : 'block'}>
                <Div>
                  <Button
                    mt={isMobile() ? '16px!important' : null}
                    onClick={
                      shouldOpenApplyModal
                        ? () => shouldShowModal(!showConfirmationModal)
                        : handleSubmit
                    }
                    variant="primarySmall"
                    className="buildButton"
                  >
                    {type === 'solarPanels'
                      ? 'Offerte aanvragen'
                      : isOverstappen
                        ? 'Aanvragen'
                        : 'Ga verder'}
                  </Button>
                </Div>
                {['home', 'travel'].includes(type) && (
                  <Window
                    type="modal"
                    show={showConfirmationModal}
                    title={`Heb je een ${providerName} rekening?`}
                    onClose={hideModal}
                    showIcon
                    modalProps={{
                      show: showConfirmationModal,
                      modalContainerDivProps: { width: '400px !important' },
                      children: (
                        <ConfirmationContent
                          providerName={providerName}
                          showDiscountTile={showDiscountTile}
                          isMobile={isMobile}
                          handleSubmit={handleSubmit}
                          showModal={hideModal}
                          data={data}
                        />
                      ),
                    }}
                  >
                    <ConfirmationContent
                      providerName={providerName}
                      showDiscountTile={showDiscountTile}
                      isMobile={isMobile}
                      handleSubmit={handleSubmit}
                      showModal={hideModal}
                      data={data}
                    />
                  </Window>
                )}
              </Div>
            ) : (
              <NonPurchasableText type={type} />
            )}
          </>
        )}
      </Div>
    );
  },
);

export const CompareCheckboxDesktop = (data: any, onChange) => {
  return (
    <Desktop>
      <Div display="flex" alignItems="center" justifyContent="left" mt={[2]}>
        <Checkbox
          mr={2}
          variant="main"
          label="Vergelijk"
          id={`compare-id-${data?.id}`}
          labelPosition="right"
          containerProps={{
            fontFamily: "'Segoe UI', sans-serif",
          }}
          checked={data.compare}
          onChange={(e) => {
            window?.dataLayer?.push({
              event: 'compare_product',
              event_action: 'Checked',
              event_label: 'Vergelijk',
              event_category: 'Funnel - HealthCare',
              component: 'Product or Top 3 product',
              comparison_id: '14081a82-217a-4927-b187-656ab23dbcca',
            });
            onChange(e, data.id);
          }}
        />
      </Div>
    </Desktop>
  );
};

export const CompareCheckboxMobile = ({
  onMobileCompare,
  data,
  extraStyles,
}: {
  onMobileCompare: (id: string) => void;
  data: any;
  extraStyles?: any;
}) => {
  return (
    <Checkbox
      mr={'8px'}
      variant="main"
      label="Vergelijk"
      id={`compare-id-${data?.id}`}
      labelPosition="right"
      containerProps={{
        fontFamily: "'Segoe UI', sans-serif",
        padding: '4px 16px',
        ...extraStyles,
      }}
      checked={data.compare}
      onChange={() => {
        onMobileCompare?.(data.id);
      }}
    />
  );
};

export const ProductNew: React.FC<ProductNewProps> = ({
  data,
  priceMode,
  promotionModals,
  additionalInfo,
  sustainabilityProps,
  priceProps,
  uspsProps,
  nextIndex,
  showSubscriptionName,
  productTypes,
  tariffsTab,
  multiCompare,
  tabsContent,
  customProductContent,
  hideTestoordeel = false,
  comparatorName,
  getTabs,
  hideProductUsps,
  indexInProductList,
  registerAnalyticsViewItemEvent,
  ...restProps
}): any => {
  const [expandedPartStartIndex, setExpandedPartStartIndex] = useState(0);
  const [expandedPartSelectedIndex, setExpandedPartSelectedIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [currentModalTab, setCurrentModalTab] = useState(-1);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const coverageTypeField = useField('coverages.coveragetype');
  const filtersField = useField('overview.filters');
  const isOverstappen =
    comparatorName.includes('os-') ||
    comparatorName === 'moneysure-event-based';
  const welcomeDiscount = data?.customAttributes?.find(
    (el) => el.description === 'Welcome Discount',
  );
  const showDiscountTile = welcomeDiscount !== undefined;

  const { trackTabClick } = useTabTracking();
  const { state } = usePage();
  const onCompareChange = state?.onCompareChange as Function;
  const onMobileCompare = (productId: string) => {
    if (window.matchMedia('(max-width: 767px)').matches && multiCompare) {
      onCompareChange(null, productId);
    }
  };
  useEffect(() => {
    if (isOpen) {
      trackTabClick(expandedPartSelectedIndex, 'product');
    }
  }, [isOpen, expandedPartSelectedIndex]);
  const { wrapperClass, toggleLabelDestkop, toggleLabelMobile } =
    getClassesByState(isOpen);

  const {
    affiliateUrl,
    compare,
    image,
    priceInfo,
    purchasableProduct,
    review,
    testScore,
    usps,
    provider,
    orderType,
    type,
    scoreEntity,
    productType,
    incentive,
  } = data;

  const hasPromotionBar =
    orderType !== 'NOT_ORDERABLE' &&
    (orderType !== 'AFFILIATE' ||
      (orderType === 'AFFILIATE' && comparatorName === 'os-health')) &&
    purchasableProduct &&
    comparatorName !== 'moneysure-event-based';

  const shouldOpenNewTab =
    affiliateUrl &&
    ['home', 'car', 'travel', 'health', 'savings', 'os-triple-play'].includes(
      type,
    );

  const registerAnalyticsAddToCartEvent =
    restProps.registerAnalyticsAddToCartEvent;

  const registerInPeregrineEvent = restProps.registerInPeregrineEvent;
  const checkoutInPeregrineEvent = restProps.checkoutInPeregrineEvent;

  const { name: providerName, id: providerId } = provider;
  const shouldOpenApplyModal =
    (type === 'home' &&
      (providerName === 'ABN AMRO' ||
        providerId === 'krn:kizi:product-supplier:ccfe7b49' ||
        providerName === 'ING' ||
        providerId === 'krn:kizi:product-supplier:41f20d22')) ||
    (type === 'travel' &&
      (providerName === 'ING' ||
        providerId === 'krn:kizi:product-supplier:41f20d22')) ||
    (comparatorName === 'os-health' &&
      orderType === 'AFFILIATE' &&
      incentive !== null);

  const handleSubmit = useHandleSubmitClick(
    shouldOpenNewTab,
    data,
    nextIndex,
    registerAnalyticsAddToCartEvent,
    registerInPeregrineEvent,
    checkoutInPeregrineEvent,
  );

  const { discountIncluded, initial, total } = priceInfo;

  const testScoreValue = useMemo(() => {
    const defaultValue = '0,0';

    if (provider.type === 'risk' && type === 'home') {
      const filters = JSON.parse(filtersField?.value as string);
      const allRisk = filters['all_risk'][0] === '0';
      const coverageType = coverageTypeField?.value;

      try {
        return getRiskProductTestScore(
          coverageType,
          allRisk,
          data.supplierCustomAttributes,
        );
      } catch (error) {
        console.error(error);
        return '-,-';
      }
    }

    return testScore !== undefined ? testScore.toFixed(1) : defaultValue;
  }, [testScore, type, scoreEntity]);

  const hideTestscore =
    (type === 'energy' || type === 'energyAuction' || type === 'moneysure') &&
    (testScoreValue === '0.0' || testScoreValue === '0,0');

  let priceInitialValue = initial;
  let priceValue = total;

  if (type === 'energyAuction') {
    priceInitialValue = data.price[priceMode].total;
    priceValue = data.price[priceMode].total;
  }

  function openTab(index: number): void {
    if (isOpen) {
      setExpandedPartSelectedIndex(index);
    } else {
      setIsOpen(true);
      setExpandedPartStartIndex(index);
    }
  }

  const setModalTab0 = () => setCurrentModalTab(0);
  const setModalTab1 = () => setCurrentModalTab(1);
  const setModalTab2 = () => setCurrentModalTab(2);
  const setModalTab3 = () => setCurrentModalTab(3);
  const toggleOpenState = () => {
    if (!isOpen) {
      sendCustomEvent('QuoteSelected', {
        price: total,
        provider: provider.name,
      });

      if (registerAnalyticsViewItemEvent) {
        registerAnalyticsViewItemEvent(data, indexInProductList);
      }
    }
    setExpandedPartStartIndex(0);
    setExpandedPartSelectedIndex(
      type === 'energyAuction' && !isMobile() ? 2 : 0,
    );
    setIsOpen(!isOpen);
  };

  const sustainabilityTabIndex = sustainabilityProps?.tabToOpen ?? 3;

  const productUspsContent: ProductUspsProps = {
    data:
      type === 'savings' && isMobile()
        ? [
            ...usps,
            { text: `Garantiestelsel: ${data?.garantiestelsel}`, type: 'pro' },
          ]
        : usps,
    onItemClick: () => {
      openTab(1);
    },
  };

  const testScoreLabel = 'Testoordeel';

  const ExpandedComponent = (
    <ExpandedPart
      data={data}
      onTabChange={(i) => setExpandedPartSelectedIndex(i)}
      priceMode={priceMode}
      setModalTab0={setModalTab0}
      setModalTab1={setModalTab1}
      setModalTab2={setModalTab2}
      setModalTab3={setModalTab3}
      selectedTabIndex={expandedPartSelectedIndex}
      startIndex={expandedPartStartIndex}
      purchasableProduct={purchasableProduct}
      tariffsTab={tariffsTab}
      tabsContent={tabsContent}
      hideTestoordeel={hideTestoordeel}
      getTabs={getTabs}
      hideProductUsps={hideProductUsps}
      indexInProductList={indexInProductList}
    />
  );

  // Entry point for custom card coming from each comparator.
  if (customProductContent) {
    return getRendererFromDescriptor({
      type: customProductContent,
      props: {
        data,
        productType,
        openTab: (index) => openTab(index),
        promotionModals,
        hasPromotionBar,
        productTypes,
        showDiscountTile,
        multiCompare,
        isOpen,
        priceMode,
        registerAnalyticsAddToCartEvent,
        nextIndex,
        shouldOpenApplyModal,
        ExpandedComponent,
        toggleOpenState,
        onCompareChange,
        onMobileCompare,
        setShowConfirmationModal,
        showConfirmationModal,
        handleSubmit,
        productUspsContent,
        uspsProps,
        currentModalTab,
        setCurrentModalTab,
        wrapperClass,
        toggleLabelDestkop,
        toggleLabelMobile,
        isOverstappen,
        additionalInfo,
        showSubscriptionName,
        hideTestscore,
        testScoreValue,
        welcomeDiscount,
        providerName,
        hideModal: () => setShowConfirmationModal(false),
        indexInProductList,
      },
    });
  }

  return (
    <Div position="relative" {...restProps}>
      <Div
        data-testid="product-card"
        className={wrapperClass}
        display="flex"
        flexDirection="column"
        variant={'product'}
        pb={[0, null, 2]}
        css={{
          outline: data.compare ? '2px solid #004DF5' : '',
        }}
      >
        {promotionModals && hasPromotionBar && (
          <PromotionBar
            incentive={data.incentive}
            productTypes={productTypes}
            items={promotionModals.items}
          />
        )}

        <Div
          px={[3, null, 5]}
          pt={[3, null, 5]}
          pb={[3, null, 0]}
          display="flex"
          flexDirection={['column', 'row']}
        >
          <Div
            className="alwaysVisible"
            display={[null, null, 'flex']}
            gridGap={type === 'solarPanels' ? '1rem' : null}
            width="100%"
          >
            <Div
              display="flex"
              justifyContent="space-between"
              alignItems={[showDiscountTile ? 'end' : null, 'unset']}
            >
              <Div
                display="flex"
                variant="productLeftCol"
                flexDirection="column"
                justifyContent="center"
              >
                <Div
                  display="flex"
                  variant="productLogoWrapper"
                  mb={[null, null, 5]}
                >
                  <Div
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    variant={
                      !purchasableProduct && isOverstappen
                        ? 'grayedContainer'
                        : ''
                    }
                  >
                    <ProductLogo
                      image={image}
                      width={provider.name === 'rhion' ? '140%' : '100%'}
                    />
                    {showDiscountTile && (
                      <Div mt={2}>
                        <DiscountTile discount={welcomeDiscount} />
                      </Div>
                    )}
                  </Div>
                </Div>

                {review && !['energyAuction'].includes(type) && (
                  <>
                    <RatingReviews
                      display={['block', null, 'none']}
                      data={review}
                    />
                    <RatingReviews
                      display={['none', null, 'block']}
                      data={review}
                      onReviewsClick={() => openTab(3)}
                    />
                  </>
                )}

                {!isMobile() && (
                  <Div
                    variant={
                      !purchasableProduct && isOverstappen
                        ? 'grayedContainer'
                        : ''
                    }
                  >
                    {multiCompare &&
                      CompareCheckboxDesktop(data, onCompareChange)}
                  </Div>
                )}
              </Div>
              <Div display={['flex', null, 'none']}>
                <PriceButtonComp
                  type={type}
                  discountIncluded={discountIncluded}
                  priceInitialValue={priceInitialValue as number}
                  priceMode={priceMode}
                  priceValue={priceValue}
                  data={data}
                  priceProps={data}
                  purchasableProduct={purchasableProduct}
                  shouldOpenApplyModal={shouldOpenApplyModal}
                  shouldShowModal={setShowConfirmationModal}
                  hideModal={() => setShowConfirmationModal(false)}
                  showConfirmationModal={showConfirmationModal}
                  handleSubmit={handleSubmit}
                  comparatorName={comparatorName}
                  providerName={providerName}
                  isOverstappen={isOverstappen}
                  showDiscountTile={showDiscountTile}
                  hideCTAButton={false}
                />
              </Div>
            </Div>

            <Div
              display="flex"
              justifyContent={
                type === 'energyAuction' ? 'space-between' : 'unset'
              }
              flexDirection={isMobile() ? 'column' : null}
              gridGap={isMobile() ? 3 : null}
            >
              <Div className="leftCol">
                {showSubscriptionName && data?.subscriptionName && (
                  <P variant="footnoteImportant" mb={2}>
                    {data.subscriptionName}
                  </P>
                )}
                <ProductUsps {...productUspsContent} {...uspsProps} />
              </Div>
              <Div
                className="rightCol"
                display={['none', 'none', 'flex']}
                flexDirection="column"
                variant="productRightCol"
                width={isMobile() ? '100%' : 'auto'}
              >
                <Div display="flex">
                  {!additionalInfo ? (
                    <>
                      {type !== 'energyAuction' && (
                        <>
                          <Price
                            display={['none', 'inline-flex']}
                            onLabelClick={() => openTab(1)}
                            {...getBigProductFeature(data, priceMode)}
                            {...priceProps}
                          />
                          <Price
                            display={[null, 'none']}
                            onDivElementClick={() => openTab(1)}
                            {...getBigProductFeature(data, priceMode)}
                            variant="priceMediumWithoutBorder"
                            {...priceProps}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <AdditionalInfo
                      {...additionalInfo}
                      value={get(data, additionalInfo.path ?? '')}
                      data={data}
                    />
                  )}
                  {!hideTestscore && (
                    <Div
                      width={isMobile() ? '100%' : 'auto'}
                      display="flex"
                      flexDirection={isMobile() ? 'row' : 'column'}
                      gridGap={isMobile() ? 5 : 2}
                      mr="0!important"
                    >
                      <ButtonCard
                        iconName="own-risk"
                        onClick={() => openTab(1)}
                        value={`€ ${data?.bigProductFeature?.value[priceMode]}`}
                        label="Eigen risico"
                        tooltip="Het eigen risico is het bedrag van een schade dat de verzekeringsmaatschappij niet vergoedt en dus voor rekening van de verzekerde komt."
                      />
                      <ButtonCard
                        iconName={isOverstappen ? 'success-ribbon' : 'favicon'}
                        onClick={() => openTab(3)}
                        value={getDisplayedScore(testScoreValue)}
                        label={isOverstappen ? 'Polisscore' : 'Testoordeel'}
                        tooltip={
                          isOverstappen
                            ? `Overstappen beoordeelt ${
                                comparatorName === 'os-travel'
                                  ? 'reisverzekeringen'
                                  : 'woonverzekeringen'
                              } op de kwaliteit van de polisvoorwaarden. De belangrijkste dekkingsonderdelen krijgen van onze experts een score en een wegingspercentage, zo komt de polisscore tot stand.`
                            : `De Consumentenbond beoordeelt ${
                                comparatorName === 'travel'
                                  ? 'reisverzekeringen'
                                  : 'woonverzekeringen'
                              } op de kwaliteit van de polisvoorwaarden.`
                        }
                      />
                    </Div>
                  )}
                  {!isOverstappen && !hideTestscore && (
                    <Sustainability
                      display={['none', 'inline-flex']}
                      onLabelClick={() => openTab(sustainabilityTabIndex)}
                      score={testScoreValue}
                      text={testScoreLabel}
                      iconName="favicon"
                      {...sustainabilityProps}
                    />
                  )}
                  {!isOverstappen && !hideTestscore && (
                    <Sustainability
                      display={['inline-flex', 'none']}
                      onDivElementClick={() => openTab(sustainabilityTabIndex)}
                      score={testScoreValue}
                      text={testScoreLabel}
                      iconName="favicon"
                      {...sustainabilityProps}
                    />
                  )}
                </Div>
                {type === 'solarPanels' && (
                  <Div
                    display={['none', 'none', 'flex']}
                    flexDirection="column"
                    gridGap={3}
                  >
                    <Requirement
                      iconName="bolt"
                      value={`${data?.yearlyGenerationInKwh} kWh`}
                      label="Opwekking p.j."
                      tooltip="De opwekking wordt berekend op basis van het aantal panelen x het aantal Wattpiek, rekening houdend met de powerfactor. Daarnaast wordt er ook rekening gehouden met de dakligging en het vermogensverlies."
                    />
                    <Requirement
                      iconName="piggyBankNew"
                      value={`€ ${String(
                        data?.savingBenefits?.[0]?.returnOnInvestmentAfter1year,
                      ).replace('.', ',')},-`}
                      label="Besparing p.j."
                      tooltip='Totale besparing per jaar = "Opbrengst zonnepanelen in kWh * het gemiddelde stroomtarief. (wij gaan uit van een gemiddelde opbrengst van 370 kWh per paneel)'
                    />
                    <Requirement
                      iconName="newStopWatch"
                      value={`${String(
                        data?.savingBenefits?.[0]?.returnOnInvestmentInYears,
                      ).replace('.', ',')} Jaar`}
                      label="Terugverdientijd"
                      tooltip="De verwachte terugverdientijd = Aanschafkosten / totale besparing per jaar"
                    />
                  </Div>
                )}
                {!['energyAuction'].includes(type) && (
                  <Div>
                    <ProductUsps {...productUspsContent} showConsPros={false} />
                  </Div>
                )}
              </Div>
            </Div>
            {type === 'solarPanels' && (
              <Div
                pb={3}
                display={['flex', 'flex', 'none!important']}
                flexWrap="wrap"
                borderBottom="1px solid !important"
                borderBottomColor="#D8D8D8!important"
                gridGap="4px!important"
              >
                <Requirement
                  iconName="bolt"
                  value={`${data?.yearlyGenerationInKwh} kWh`}
                  label="Opwekking p.j."
                />
                <Requirement
                  iconName="piggyBankNew"
                  value={`€ ${String(
                    data?.savingBenefits?.[0]?.returnOnInvestmentAfter1year,
                  ).replace('.', ',')},-`}
                  label="Besparing p.j."
                />
                <Requirement
                  iconName="newStopWatch"
                  value={`${String(
                    data?.savingBenefits?.[0]?.returnOnInvestmentInYears,
                  ).replace('.', ',')} Jaar`}
                  label="Terugverdientijd"
                />
              </Div>
            )}
            <Div>
              <Div
                display={['flex', null, 'none']}
                width={'100%'}
                justifyContent={'space-between'}
              >
                <Div
                  data-testid="toggle-product-mobile"
                  display="flex"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleOpenState();
                  }}
                >
                  <Div display="flex" my="auto">
                    {toggleLabelMobile}
                  </Div>
                  <Arrow isOpen={isOpen} ml={1} my="auto" />
                </Div>

                {type !== 'energyAuction' && multiCompare && (
                  <Div onClick={() => onMobileCompare(data.id)}>
                    {compare
                      ? 'Verwijder van vergelijking'
                      : 'Voeg toe aan vergelijking'}
                  </Div>
                )}
              </Div>

              <Div display={['none', null, 'flex']} flexDirection="column">
                <PriceButtonComp
                  type={type}
                  discountIncluded={discountIncluded}
                  priceInitialValue={priceInitialValue as number}
                  priceMode={priceMode}
                  priceValue={priceValue}
                  data={data}
                  priceProps={data}
                  purchasableProduct={purchasableProduct}
                  shouldOpenApplyModal={shouldOpenApplyModal}
                  shouldShowModal={setShowConfirmationModal}
                  hideModal={() => setShowConfirmationModal(false)}
                  showConfirmationModal={showConfirmationModal}
                  handleSubmit={handleSubmit}
                  comparatorName={comparatorName}
                  providerName={providerName}
                  isOverstappen={isOverstappen}
                  showDiscountTile={showDiscountTile}
                />
              </Div>
            </Div>
          </Div>
        </Div>
        {isOpen && ExpandedComponent}
        {type === 'energyAuction' && (
          <ProductOverviewModal
            startIndex={currentModalTab}
            onClose={(e) => {
              e.stopPropagation();
              setCurrentModalTab(-1);
            }}
            priceMode={priceMode}
            product={data}
            onSubmit={handleSubmit}
          />
        )}
      </Div>
      <Div position="relative">
        <Button
          data-testid="toggle-product-desktop"
          display={['none', null, 'flex']}
          mx="auto"
          mt="-15px"
          onClick={toggleOpenState}
          variant={
            ['solarPanels'].includes(type)
              ? 'moreInformationDark'
              : 'moreInformation'
          }
        >
          {toggleLabelDestkop}
        </Button>
      </Div>
    </Div>
  );
};

type ProductLogoProps = {
  image: string;
  width?: string;
  margin?: string;
  css?: any;
};

const isFirefox = (): boolean => /firefox/i.test(navigator.userAgent);

export const ProductLogo = ({
  image,
  margin,
  width = '100%',
  ...restProps
}: ProductLogoProps): JSX.Element => {
  const firefoxStyle = isFirefox() ? { width: '80px' } : { width, margin };
  return image ? (
    <Image
      alt={`logo-${image}`}
      height="100%"
      src={image}
      width={width}
      style={firefoxStyle}
      {...restProps}
    />
  ) : (
    <Div height={40} {...restProps} />
  );
};

export interface ProductsNewProps extends Omit<ProductNewProps, 'data'> {
  productList: any;
  promotionBarModals?: PromotionBarProps;
  defaultFilters: string;
  noProductsDescriptor?: ComponentDescriptorOrArray;
  productTypes: Array<string>;
  tariffsTab?: string;
  comparatorName: string;
  coveragesBlockMinHeight?: number | 0;
}

export const ProductsNew: React.FC<ProductsNewProps> = ({
  priceMode = 'month',
  productList = [],
  defaultFilters,
  promotionBarModals,
  noProductsDescriptor,
  productTypes,
  tariffsTab,
  comparatorName,
  onChange,
  ...restProps
}) => {
  const [visibleProductsCount, setVisibleProductsCount] = useState(0);
  const defaultFiltersField = useField(defaultFilters);
  const filterFields = useField('overview.filters');

  const products = productList.map((p, i) => {
    const isFirst = i === 0;

    const _props: ProductNewProps = {
      data: p,
      comparatorName,
      ...restProps,
      mt: [4, 30],
      priceMode,
      productTypes,
    };

    if (isFirst) {
      _props.mt = 0;
    }

    const TariffsTab: React.ReactNode[] = [];
    if (tariffsTab) {
      TariffsTab.push(
        getRendererFromDescriptor({
          type: tariffsTab,
          props: {
            data: p,
            priceMode: priceMode,
          },
        }),
      );
    }

    return (
      <ProductNew
        key={'product-' + p?.id}
        promotionModals={promotionBarModals}
        tariffsTab={TariffsTab}
        {..._props}
        indexInProductList={i}
      />
    );
  });

  const productsLength: number = products.length;

  useEffect(() => {
    setVisibleProductsCount(productsLength >= 10 ? 10 : productsLength);
  }, [productList, productsLength]);

  function handleFilterReset() {
    filterFields?.set({ value: defaultFiltersField?.value as string });
    const _filterFieldsValue = defaultFiltersField?.value as string;
    onChange && onChange(JSON.parse(_filterFieldsValue));
  }

  let component;

  if (productsLength == 0 && noProductsDescriptor === undefined) {
    component = <ResetFiltersBlock onResetFilters={handleFilterReset} />;
  } else if (productsLength == 0 && noProductsDescriptor !== undefined) {
    component = <>{getRendererFromDescriptor(noProductsDescriptor)}</>;
  } else
    component = (
      <>
        {Array.from(Array(visibleProductsCount).keys()).map((i) => {
          return products[i];
        })}
        <Div
          width={'fit-content'}
          mx={'auto'}
          mt={7}
          hidden={visibleProductsCount >= productsLength}
        >
          <Button
            variant={'secondaryTransparent'}
            onClick={() =>
              addMoreProducts(
                productsLength,
                visibleProductsCount,
                setVisibleProductsCount,
              )
            }
          >
            Toon meer producten
          </Button>
        </Div>
      </>
    );

  return component;
};

export interface ProductsNewWrapperProps extends ProductsNewProps {
  filters: string;
  defaultFilters: string;
  disableLoadingSkeleton?: boolean;
  isLoadingField?: string;
  tariffsTab?: string;
  wrapperStyles?: any;
}

export const ProductsNewWrapper: React.FC<ProductsNewWrapperProps> = ({
  filters,
  defaultFilters,
  disableLoadingSkeleton = false,
  isLoadingField = 'overview.isLoadingProducts',
  tariffsTab,
  ...restProps
}) => {
  const { name } = useContext(ComparatorContext);
  const { state } = usePage();
  const isLoadingProducts = useField(isLoadingField);
  const filtersField = useField(filters);

  const onChange = useCallback(
    (filters) => {
      (state?.onChange as Function)(filters);
    },
    [state?.onChange],
  );

  const productList = useMemo(
    () => (state?.productList as any[] || [])?.filter(p=>p.id !== '') ?? [],
    [state?.productList],
  );

  const _filters = JSON.parse((filtersField?.value as string) || '[]');

  if (!disableLoadingSkeleton && isLoadingProducts?.value === 'true') {
    return (
      <>
        {new Array(5).fill(null).map((_, idx) => (
          <ProductSkeletonCard key={`loader-${idx}`} />
        ))}
      </>
    );
  }

  return (
    <ProductsNew
      {...restProps}
      {..._filters}
      defaultFilters={defaultFilters}
      productList={productList}
      priceMode={_filters.showPriceMode?.[0] || 'month'}
      onChange={onChange}
      tariffsTab={tariffsTab}
      comparatorName={name}
    />
  );
};

export default ProductsNewWrapper;
